import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getSocket } from "../lib/socket";
import { useIsTouchDevice } from "../lib/useIsTouchDevice";
import { useViewport } from "../lib/useViewport";
import { useVoiceChat } from "../lib/useVoiceChat";
import { useRoomStore } from "../state/useRoomStore";
import { useGameCatalogStore } from "../state/useGameCatalogStore";
import { mountGame, type MountedGame } from "../games/registry";
import { TouchControls } from "../games/rette-anna/TouchControls";
import { SettingsOverlay } from "../components/SettingsOverlay";

export function GamePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<MountedGame | null>(null);
  const room = useRoomStore((s) => s.room);
  const getTitle = useGameCatalogStore((s) => s.getTitle);

  const isTouch = useIsTouchDevice();
  const viewport = useViewport();
  // Nur echte Touch-Geräte mit schmalem Viewport bekommen automatisch Vollbild.
  const isMobileTouch = isTouch && viewport.width < 900;

  const [fullscreen, setFullscreen] = useState<boolean>(isMobileTouch);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const voice = useVoiceChat();

  useEffect(() => {
    if (isMobileTouch) setFullscreen(true);
  }, [isMobileTouch]);

  useEffect(() => {
    document.body.classList.toggle("in-game", fullscreen);
    const id = window.setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
    return () => {
      window.clearTimeout(id);
      document.body.classList.remove("in-game");
    };
  }, [fullscreen]);

  useEffect(() => {
    const id = window.setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
    return () => window.clearTimeout(id);
  }, [viewport.orientation, viewport.width, viewport.height]);

  useEffect(() => {
    if (!room) {
      navigate("/");
      return;
    }
    const container = containerRef.current;
    if (!container) return;

    const socket = getSocket();
    const mounted = mountGame(room.gameId, container, { socket, room });
    gameRef.current = mounted;

    return () => {
      mounted?.destroy();
      gameRef.current = null;
    };
  }, [room, navigate]);

  const leave = useCallback(() => {
    voice.disable();
    getSocket().emit("room:leave");
    useRoomStore.getState().setRoom(null);
    navigate("/");
  }, [navigate, voice]);

  const toggleFullscreen = useCallback(async () => {
    setFullscreen((prev) => !prev);
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch {
      // Browser kann Fullscreen-API verweigern – egal, CSS-Fullscreen reicht
    }
  }, []);

  return (
    <div className={`game-page ${fullscreen ? "fullscreen" : ""}`}>
      <div className="game-toolbar">
        <div className="meta">
          <h2>{room ? getTitle(room.gameId) : "Spiel"}</h2>
          <p>
            Raum {code} · {room?.players.length ?? 0} Spieler
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flex: "0 0 auto", flexWrap: "wrap" }}>
          {voice.enabled && (
            <button
              type="button"
              className={`settings-fab ${voice.muted ? "" : "active"}`}
              onClick={voice.toggleMute}
              title={voice.muted ? "Mikro einschalten" : "Stummschalten"}
              style={voice.muted ? { borderColor: "var(--danger)", color: "var(--danger)" } : {}}
            >
              {voice.muted ? "🔇" : voice.speaking ? "🎤" : "🎙️"}
            </button>
          )}
          <button
            type="button"
            className="settings-fab"
            onClick={() => setSettingsOpen(true)}
            aria-label="Einstellungen"
            title="Ton & Voice-Chat"
          >
            ⚙️
          </button>
          {!isMobileTouch && (
            <button className="btn btn-ghost" onClick={toggleFullscreen}>
              {fullscreen ? "Verkleinern" : "Vollbild"}
            </button>
          )}
          <button
            type="button"
            className="settings-fab"
            onClick={leave}
            aria-label="Verlassen"
            title="Spiel verlassen"
            style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Speaking-Indikatoren über dem Spiel */}
      {voice.enabled && voice.peers.length > 0 && (
        <div className="voice-bar voice-bar-floating">
          {voice.peers.map((peer) => {
            const player = room?.players.find((p) => p.id === peer.id);
            return (
              <span
                key={peer.id}
                className={`voice-peer ${peer.speaking ? "speaking" : ""}`}
              >
                <span
                  className="voice-dot"
                  style={{ background: player?.color ?? "#888" }}
                />
                {player?.name ?? "Spieler"}
              </span>
            );
          })}
        </div>
      )}

      <div className="game-canvas-wrap">
        <div ref={containerRef} className="game-canvas" />
        {isTouch && <TouchControls layout="overlay" />}
      </div>

      <SettingsOverlay
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        voice={voice}
        room={room}
      />
    </div>
  );
}
